import {
	Dispatch,
	FC,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	createRef,
	forwardRef,
	useState,
	useImperativeHandle,
} from "react";
import SignatureCanvas from "react-signature-canvas";
import TextField from "elements/Input/TextField";
import Cross from "assets/imgs/close.svg";
import SignatureSpinner from "public/signature-spinner.svg";
import SignatureCheck from "public/signature-check.svg";
import { Checkbox, Label } from "components";
import ButtonV2 from "./ButtonV2";

interface SignatureBoxProps {
	disableBtn?: boolean;
	btnText?: string;
	submitting: boolean;
	onSubmit: (
		signatureData: string,
		additionalData: { usedTypeToSign: boolean }
	) => void;
	canUseType?: boolean;
	smsConsent?: boolean;
	setSmsConsent?: Dispatch<SetStateAction<boolean>>;
	authorizedAs?: string;
	showSubmitButton?: boolean;
	onDisabledChange?: (disabled: boolean) => void;
	children?: React.ReactNode;
	className?: string;
	contactInfo?: {
		firstName: string;
		lastName: string;
	};
}

const formatContactInfo = (contactInfo: SignatureBoxProps["contactInfo"]) => {
	if (!contactInfo) return "";
	return `${contactInfo?.firstName ?? ""} ${contactInfo?.lastName ?? ""}`
		.replace(/\s+/g, " ")
		.trim();
};

export const useSignatureSubmit = ({
	onSubmit,
	contactInfo,
	canUseType = false,
}: Pick<SignatureBoxProps, "onSubmit" | "contactInfo" | "canUseType">) => {
	const [signed, setSigned] = useState(false);
	const [startedSign, setStartedSign] = useState(false);
	const [signedError, setSignedError] = useState(false);
	const [signatureType, setSignatureType] = useState<"draw" | "type">(
		canUseType ? "type" : "draw"
	);
	const [typedSignature, setTypedSignature] = useState("");

	const [windowWidth, setWindowWidth] = useState(0);

	const canvasWrapperRef = useRef<HTMLDivElement>(null);

	const typeDisplayRef = useRef<HTMLCanvasElement>(null);
	const sigPadRef = useRef<SignatureCanvas>(null);

	useEffect(() => {
		const handler = () => {
			if (!canvasWrapperRef.current) return;

			const width = canvasWrapperRef.current.clientWidth;

			canvasWrapperRef.current.style.height = `${width / 3.333333}px`;
		};

		handler();

		window.addEventListener("resize", handler);

		return () => {
			window.removeEventListener("resize", handler);
		};
	}, [signatureType]);

	const clearSignature = () => {
		if (sigPadRef.current) {
			sigPadRef.current.clear();
			setSigned(false);
			setSignedError(false);
			setStartedSign(false);
		}
	};

	useEffect(() => {
		if (!typeDisplayRef.current) return;
		const xOffset = 60;
		const canvas = typeDisplayRef.current;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.fillStyle = "white";
		ctx.fillRect(0, 0, canvas.width, canvas.height);
		canvas.width = 500;
		canvas.height = 150;
		canvas.style.aspectRatio = "3.333";
		let fontSize = 100;
		ctx.font = `${fontSize}px "turbinado-pro"`;
		let textWidth = ctx.measureText(typedSignature);

		while (textWidth.width + xOffset * 2 > canvas.width) {
			fontSize -= 1;
			ctx.font = `${fontSize}px "turbinado-pro"`;
			textWidth = ctx.measureText(typedSignature);
		}

		ctx.fillStyle = "#000";
		ctx.fillText(typedSignature, xOffset, canvas.height * 0.75);
	}, [typeDisplayRef.current, typedSignature]);

	useEffect(() => {
		if (signatureType === "type") {
			sigPadRef.current?.clear();
		}

		if (signatureType === "draw") {
			setTypedSignature("");
		}

		setSigned(false);
	}, [signatureType]);

	useEffect(() => {
		if (signatureType === "type") {
			if (typedSignature) {
				setStartedSign(true);
			} else {
				setStartedSign(false);
			}
		}
	}, [typedSignature, signatureType]);

	useEffect(() => {
		if (typeof window !== "undefined") {
			setWindowWidth(window.innerWidth);
		}
	}, []);

	const resizeHandler = useCallback(() => {
		if (!sigPadRef.current) return;

		if (windowWidth && window.innerWidth !== windowWidth) {
			//@ts-ignore
			sigPadRef.current._resizeCanvas();

			clearSignature();
			setWindowWidth(window.innerWidth);
		}
	}, [windowWidth, sigPadRef.current]);

	useEffect(() => {
		if (typeof window === "undefined" || !sigPadRef.current) {
			return;
		}

		resizeHandler();
		window.addEventListener("resize", resizeHandler);

		return () => {
			window.removeEventListener("resize", resizeHandler);
		};
	}, [sigPadRef, resizeHandler]);

	useEffect(() => {
		if (
			signatureType === "type" &&
			typedSignature.toUpperCase() ===
				formatContactInfo(contactInfo).toUpperCase()
		) {
			setSigned(true);
		} else {
			setSigned(false);
		}
	}, [typedSignature, contactInfo]);

	const endCanvasStroke = (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (sigPadRef.current) {
			const signDots = sigPadRef.current.toData();
			let numDots = 0;
			for (let i = 0; i < signDots.length; i++) {
				numDots += signDots[i].length;
			}
			if (numDots > 38) {
				setSigned(true);
				setSignedError(false);
			}
		}
	};

	const typedSignatureError = useMemo(() => {
		if (!contactInfo) return false;

		if (signatureType === "type") {
			const target = formatContactInfo(contactInfo).toUpperCase();

			if (typedSignature && !target.startsWith(typedSignature.toUpperCase())) {
				return true;
			}
		}

		return false;
	}, [typedSignature, signatureType, contactInfo]);

	const handleSubmitClick = (): string | undefined => {
		if (signatureType === "draw") {
			if (!sigPadRef.current) return;
			const data = sigPadRef.current.toDataURL("image/png");

			const signDots = sigPadRef.current.toData();
			let numDots = 0;
			for (let i = 0; i < signDots.length; i++) {
				numDots += signDots[i].length;
			}

			if (numDots > 32) {
				onSubmit(data, { usedTypeToSign: false });
				return data;
			} else {
				clearSignature();
				return;
			}
		}

		if (signatureType === "type") {
			const destinationCanvas = document.createElement("canvas");

			destinationCanvas.width = 500;
			destinationCanvas.height = 150;

			const destCtx = destinationCanvas.getContext("2d");
			if (!destCtx) return;

			let xOffset = 20;
			let fontSize = 100;
			destCtx.fillStyle = "#FFF";
			destCtx.fillRect(0, 0, destinationCanvas.width, destinationCanvas.height);
			destCtx.font = `${fontSize}px "turbinado-pro"`;
			let textWidth = destCtx.measureText(typedSignature);

			while (textWidth.width + xOffset * 2 > destinationCanvas.width) {
				fontSize -= 1;
				destCtx.font = `${fontSize}px "turbinado-pro"`;
				textWidth = destCtx.measureText(typedSignature);
			}

			destCtx.fillStyle = "#000";
			destCtx.fillText(typedSignature, xOffset, destinationCanvas.height * 0.8);

			const data = destinationCanvas.toDataURL("image/png");

			onSubmit(data, { usedTypeToSign: true });
			return data;
		}
	};

	return {
		typeDisplayRef,
		canvasWrapperRef,
		sigPadRef,
		handleSubmitClick,
		typedSignatureError,
		endCanvasStroke,
		clearSignature,
		setSignatureType,
		signatureType,
		startedSign,
		setStartedSign,
		contactInfo,
		typedSignature,
		setTypedSignature,
		signed,
	};
};
// eslint-disable-next-line react/display-name
const SignatureBox = forwardRef<() => void, SignatureBoxProps>(
	(
		{
			submitting,
			onSubmit,
			btnText,
			canUseType = false,
			disableBtn = false,
			setSmsConsent,
			smsConsent,
			authorizedAs,
			showSubmitButton = true,
			onDisabledChange = (disabled: boolean) => {},
			children,
			contactInfo,
			className = "",
		},
		submitRef
	) => {
		const {
			typeDisplayRef,
			canvasWrapperRef,
			sigPadRef,
			handleSubmitClick,
			typedSignatureError,
			endCanvasStroke,
			clearSignature,
			setSignatureType,
			signatureType,
			startedSign,
			setStartedSign,
			typedSignature,
			setTypedSignature,
			signed,
		} = useSignatureSubmit({
			onSubmit,
			contactInfo,
			canUseType,
		});

		useImperativeHandle(submitRef, () => handleSubmitClick, [
			handleSubmitClick,
		]);

		const disabled = useMemo(
			() => submitting || !signed || disableBtn,
			[submitting, signed, disableBtn]
		);

		useEffect(() => {
			onDisabledChange(disabled);
		}, [disabled, onDisabledChange]);

		return (
			<div
				className={["appeal-legal-signature-container", className].join(" ")}>
				{children}
				{canUseType && (
					<div id="appeal-legal-signature-type-toggle-container">
						<div
							onClick={() => setSignatureType("draw")}
							className={`appeal-legal-signature-type-toggle${
								signatureType === "draw" ? "-active" : ""
							}`}>
							<p>Draw Signature</p>
						</div>
						<div
							onClick={() => setSignatureType("type")}
							className={`appeal-legal-signature-type-toggle${
								signatureType === "type" ? "-active" : ""
							}`}>
							<p>Type Signature</p>
						</div>
					</div>
				)}
				{signatureType === "draw" ? (
					<div className="appeal-sign" draggable={false}>
						<SignatureAuthorizeAs authorizedAs={authorizedAs} />
						<p className="extra-small semibold my-1">
							Please use your full legal name, including any suffix (e.g., Jr.,
							Sr., II), to ensure it matches the county's information, as any
							discrepancy will cause delays.
						</p>
						<p className="sm appeal-sign-instructions">
							Sign here by holding down your mouse or finger.
						</p>
						<SignatureCanvasContainer
							ref={canvasWrapperRef}
							startedSign={startedSign}>
							<SignatureCanvas
								clearOnResize={false}
								onBegin={() => {
									setStartedSign(true);
									if (window.getSelection) {
										window.getSelection()?.removeAllRanges();
									}
								}}
								onEnd={endCanvasStroke}
								penColor="rgba(47,98,153,1)"
								backgroundColor="rgba(255,255,255,1)"
								canvasProps={{
									className: "sigCanvas",
								}}
								ref={sigPadRef}
							/>
						</SignatureCanvasContainer>
					</div>
				) : (
					<div className="appeal-sign">
						<p
							className={
								"sm appeal-sign-instructions" +
								(typedSignatureError ? "-error" : "")
							}>
							Type "{formatContactInfo(contactInfo)}" below to sign.
						</p>
						<div className="appeal-sign-type-container">
							<TextField
								autoComplete="off"
								id={
									"appeal-type-signature" +
									(typedSignatureError ? "-error" : "")
								}
								value={typedSignature}
								onChange={e => setTypedSignature(e.target.value)}
								placeholder={formatContactInfo(contactInfo)}
							/>
						</div>
						<div className="canvas-container">
							<canvas id="appeal-type-signature-display" ref={typeDisplayRef} />
							<div className="dotted-line" />
							<div className="dotted-line-x" draggable={false}>
								{"\u2717"}
							</div>
						</div>
					</div>
				)}
				<SignatureStatus
					signed={signed}
					startedSign={startedSign}
					clearSignature={clearSignature}
					signatureType={signatureType}
				/>
				<SMSConsent smsConsent={smsConsent} setSmsConsent={setSmsConsent} />
				{showSubmitButton && (
					<div id="appeal-legal-submit-btn">
						<ButtonV2
							size="large"
							type="submit"
							disabled={disabled}
							onClick={handleSubmitClick}>
							{submitting
								? "Submitting..."
								: btnText
									? btnText
									: "Complete Appeal"}
						</ButtonV2>
					</div>
				)}
			</div>
		);
	}
);

export const SignatureAuthorizeAs: React.FC<
	Pick<SignatureBoxProps, "authorizedAs">
> = ({ authorizedAs }) => {
	if (!authorizedAs) return null;
	return (
		<div className="appeal-sign-instructions-card">
			<Label>Signature must reflect one of the legal owner’s names:</Label>
			<p>{authorizedAs}</p>
		</div>
	);
};

const SignaturePlaceholder = () => {
	return (
		<div className="appeal-legal-signature-placeholder">
			<img
				src="data:image/png;base64,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"
				style={{ opacity: 0.2, width: "100%", height: "100%" }}
			/>
		</div>
	);
};

// eslint-disable-next-line react/display-name
export const SignatureCanvasContainer = forwardRef<
	HTMLDivElement,
	Pick<ReturnType<typeof useSignatureSubmit>, "startedSign"> & {
		children: React.ReactNode;
	}
>(({ startedSign, children }, ref) => {
	return (
		<div className="canvas-container">
			<div className="canvas-wrapper" ref={ref}>
				{children}
			</div>
			{!startedSign && <SignaturePlaceholder />}
			<div className="dotted-line" />
			<div className="dotted-line-x" draggable={false}>
				{"\u2717"}
			</div>
		</div>
	);
});

export const SignatureStatus = ({
	signed,
	startedSign,
	clearSignature,
	signatureType,
}: Pick<
	ReturnType<typeof useSignatureSubmit>,
	"signed" | "startedSign" | "signatureType" | "clearSignature"
>) => {
	return (
		<div
			className={
				"appeal-sign-status-container" +
				(signed
					? " signature-complete"
					: startedSign
						? " signature-started"
						: " awaiting-signature")
			}>
			{signatureType === "draw" && startedSign && (
				<div id="appeal-legal-signature-clear" onClick={clearSignature}>
					<span>Clear Signature</span>
					<Cross />
				</div>
			)}
			{signed ? (
				<div className="row">
					<span className="signature-box-specific-font semibold">
						Signature Accepted
					</span>
					<SignatureCheck />
				</div>
			) : startedSign ? (
				<div className="row">
					<span className="signature-box-specific-font">
						{signatureType === "draw" ? "Keep Drawing" : "Signature Must Match"}
					</span>
					<SignatureSpinner className="signature-spinner" />
				</div>
			) : (
				<div className="row">
					<span className="signature-box-specific-font">
						Awaiting Signature
					</span>
					<SignatureSpinner className="signature-spinner" />
				</div>
			)}
		</div>
	);
};

export const SMSConsent = ({
	smsConsent,
	setSmsConsent,
}: Pick<SignatureBoxProps, "smsConsent" | "setSmsConsent">) => {
	if (
		typeof smsConsent !== "undefined" &&
		typeof setSmsConsent !== "undefined"
	) {
		return (
			<div className="flex w-100 signature-box-sms-consent">
				<Checkbox
					checked={smsConsent}
					onChange={e => setSmsConsent(e.target.checked)}
				/>
				<p className="body-tiny">
					I consent to receiving text message communications.
				</p>
			</div>
		);
	}

	return null;
};

export default SignatureBox;
